import { t } from '@hefe/i18n';
import {
  BasketballClubGame,
  BasketballClubScheduleGame,
  BasketballClubUpcomingGame,
  BasketballEventReplyAnswer,
  BasketballEventUserRolePlayerCoach,
  BasketballEventUserRoleType,
  BasketballGame,
  BasketballGameComment,
  BasketballGameCommentPagination,
  BasketballGameJudgeSuggestion,
  BasketballGameReply,
  BasketballGameReport,
  BasketballSeasonPlanerGame,
  BasketballTeamGameCarpool,
  BasketballTeamPlayerReplies,
  BasketballTeamPlayerReply,
  BasketballTeamTask,
  EventReport,
  EventReportEventableType,
  GymBasic,
  RequestBasePostTaskAssignments,
  RequestBasketballUpdateJudgeTeam,
  RequestBasketballUpdateJudgeUser,
  RequestBasketballUpdateOwnReferee,
  RequestBasketballUpdateResponsibleUser,
  RequestBasketballUpdateTeamGameCars,
  ResponseBasePostTaskAssignments,
  ResponseBasketballGetClubUpcomingGames,
  ResponseBasketballGetGameFormData,
  ResponseBasketballUpdateJudgeTeam,
  ResponseBasketballUpdateJudgeUser,
  ResponseBasketballUpdateOwnReferee,
  ResponseBasketballUpdateResponsibleUser,
  ResponseBasketballUpdateTeamGameCars,
  ResponseBasketballUpdateTeamGameMeetAt,
  ResponseBasketballUpdateTeamGameNominees,
  SelectOption,
  TaskAssignment,
} from '@hefe/models';
import { formatDatabaseDate } from '@hefe/utils';
import {
  PayloadAction,
  createAsyncThunk,
  createSelector,
  createSlice,
  isAnyOf,
} from '@reduxjs/toolkit';
import API from '../../../api/api';
import { BasketballEventAddCommentRequest } from '../../../models/requests/basketball-event';
import {
  BasketballGameAddCarpoolRequest,
  BasketballGameSetHomeGameRequest,
  BasketballGameSetOwnRefereesRequest,
} from '../../../models/requests/basketball-game';
import {
  BasketballSeasonPlanerAvailableWeek,
  BasketballSeasonPlanerPagination,
  BasketballSeasonPlanerRequest,
  BasketballSeasonPlanerResponse,
} from '../../../models/requests/basketball-season-planer';
import { eventReportsApi } from '../../base/event-reports/event-reports.api';
import { tasksApi } from '../../base/tasks/tasks.api';
import { updateUserBasketballGameReply } from '../../basketball/basketball-events/basketball-events.slice';
import {
  deleteBasketballGame,
  saveBasketballGame,
} from '../actions/basketball-game/basketball-game.actions';
import {
  setBasketballTeamGameHelpouts,
  setBasketballTeamGameMeetingAt,
  setBasketballTeamGameNominees,
} from '../actions/basketball-team-game/basketball-team-game.actions';
import { assignUsersToBasketballEventTask } from '../basketball-event-tasks/basketball-event-tasks.slice';

const FEATURE_KEY = 'basketballGames';

/************************************************************************************
 *
 *                                  ACTIONS
 *
 ************************************************************************************
 */

// TODO
// Idee: Hash input - check in db ob string mit

export const fetchBasketballClubGames = createAsyncThunk(
  'basketballGames/getClubGames',
  async (thunkAPI) => {
    const response = await API.basketballGames().getAll();
    return response?.data;
  }
);

export const fetchBasketballUpcomingClubGames = createAsyncThunk(
  'basketballGames/getUpcomingClubGames',
  async (thunkAPI) => {
    const response = await API.basketballGames().getUpcoming();
    return response?.data;
  }
);

export const fetchBasketballGame = createAsyncThunk(
  'basketballGames/getSingle',
  async (id: number, thunkAPI) => {
    const response = await API.basketballGames().getSingle(id);
    return response?.data;
  }
);

export const fetchBasketballSeasonPlanerGames = createAsyncThunk(
  'basketballGames/getSeasonPlanerGames',
  async (request: BasketballSeasonPlanerRequest, thunkAPI) => {
    const response = await API.basketballGames().getSeasonPlaner(
      request.weekOfYear,
      request.year
    );
    return response?.data;
  }
);

export const fetchBasketballGameFormData = createAsyncThunk(
  'basketballGames/fetchFormData',
  async (id: number, thunkAPI) => {
    const response = await API.basketballGames().getFormData(id);
    return response?.data as ResponseBasketballGetGameFormData;
  }
);

export const fetchBasketballGameTeamComments = createAsyncThunk(
  'basketballGames/getTeamComments',
  async (data: { gameId: number; teamId: number; page: number }, thunkAPI) => {
    const response = await API.basketballGameComments(data.gameId).getComments(
      data.teamId,
      data.page
    );
    return {
      data: response?.data,
      gameId: data.gameId,
      teamId: data.teamId,
    };
  }
);

export const addTeamCommentToGame = createAsyncThunk(
  'basketballGames/addTeamComment',
  async (
    data: { gameId: number; payload: BasketballEventAddCommentRequest },
    thunkAPI
  ) => {
    const response = await API.basketballGameComments(data.gameId).addComment(
      data.payload
    );
    return response?.data;
  }
);

export const addCarpoolToGame = createAsyncThunk(
  'basketballGames/addCarpool',
  async (
    data: { gameId: number; payload: BasketballGameAddCarpoolRequest },
    thunkAPI
  ) => {
    const response = await API.basketballGames()
      .carpool(data.gameId)
      .create(data.payload);
    return response?.data;
  }
);

interface DeleteCarpoolData {
  id: number;
  gameId: number;
  teamId: number;
}

export const deleteCarpoolFromGame = createAsyncThunk<any, any>(
  'basketballGames/deleteCarpool',
  async (data: DeleteCarpoolData, thunkAPI) => {
    const response = await API.basketballGames()
      .carpool(data.gameId)
      .delete(data);
    if (response) {
      return thunkAPI.fulfillWithValue(data);
    } else {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

export const downloadBasketballGameRefereeBill = createAsyncThunk(
  'basketballGames/downloadRefereeBill',
  async (id: number, thunkAPI) => {
    const response = await API.basketballGames().downloadRefereeBill(id);
    return response;
  }
);

export const setBasketballGameHomeGame = createAsyncThunk(
  'basketballGames/setHomeGame',
  async (data: BasketballGameSetHomeGameRequest, thunkAPI) => {
    const response = await API.basketballGames().setHomeGame(data);
    return response?.data;
  }
);

export const setBasketballGameSetOwnReferees = createAsyncThunk(
  'basketballGames/setOwnReferees',
  async (data: BasketballGameSetOwnRefereesRequest, thunkAPI) => {
    const response = await API.basketballGames().setOwnReferees(data);
    return response?.data;
  }
);

export const setBasketballGameJudgeTeam = createAsyncThunk(
  'basketballGames/setJudgeTeam',
  async (
    data: { gameId: number; data: RequestBasketballUpdateJudgeTeam },
    thunkAPI
  ) => {
    const response = await API.basketballGames().updateJudgeTeam(
      data.gameId,
      data.data
    );
    return response?.data;
  }
);

export const setBasketballGameJudgeUser = createAsyncThunk(
  'basketballGames/setJudgeUser',
  async (
    data: { gameId: number; data: RequestBasketballUpdateJudgeUser },
    thunkAPI
  ) => {
    try {
      const response = await API.basketballGames().updateJudgeUser(
        data.gameId,
        data.data
      );
      return response?.data;
    } catch (error) {
      throw thunkAPI.rejectWithValue(error);
    }
  }
);

export const setBasketballGameOwnReferee = createAsyncThunk(
  'basketballGames/setOwnReferee',
  async (
    data: { gameId: number; data: RequestBasketballUpdateOwnReferee },
    thunkAPI
  ) => {
    const response = await API.basketballGames().updateOwnReferee(
      data.gameId,
      data.data
    );
    return response?.data;
  }
);

export const setBasketballGameResponsibleUser = createAsyncThunk(
  'basketballGames/setResponsibleUser',
  async (
    data: { gameId: number; data: RequestBasketballUpdateResponsibleUser },
    thunkAPI
  ) => {
    const response = await API.basketballGames().updateResponsibleUser(
      data.gameId,
      data.data
    );
    return response?.data;
  }
);

export const setBasketballTeamGameCars = createAsyncThunk(
  'basketballGames/setBasketballTeamGameCars',
  async (
    data: { gameId: number; data: RequestBasketballUpdateTeamGameCars },
    thunkAPI
  ) => {
    const response = await API.basketballGames().updateCars(
      data.gameId,
      data.data
    );
    return response?.data;
  }
);

/************************************************************************************
 *
 *                                      SLICE
 *
 ************************************************************************************
 */

export interface BasketballGamesState {
  loadingGameDetails: boolean;
  gameDetails: { [key: number]: BasketballGame };
  clubGames: BasketballClubScheduleGame[];
  upcomingClubGames: BasketballClubUpcomingGame[];
  seasonPlaner: {
    currentWeek?: {
      weekOfYear: number;
      year: number;
      weekAndYear: string;
      from: string;
      to: string;
    };
    loading: boolean;
    availableWeeks: BasketballSeasonPlanerAvailableWeek[];
    pagination: BasketballSeasonPlanerPagination;
    gym_closures: {
      [key: string]: {
        [key: string]: GymBasic[];
      };
    };
    games: {
      // week -> gym -> games
      [key: string]: {
        [key: string]: { [key: string]: BasketballSeasonPlanerGame[] };
      };
    };
    visible_tasks?: SelectOption[];
  };
  judgeSuggestions: { [key: number]: BasketballGameJudgeSuggestion[] };
}

const initialState: BasketballGamesState = {
  clubGames: [],
  upcomingClubGames: [],
  gameDetails: {},
  loadingGameDetails: false,
  seasonPlaner: {
    availableWeeks: [],
    loading: false,
    games: {},
    pagination: {},
    gym_closures: {}
  },
  judgeSuggestions: {},
};

export const basketballGamesSlice = createSlice({
  name: FEATURE_KEY,
  initialState: initialState,
  reducers: {
    getAllGames: (state) => {
      state = initialState;
    },
  },
  extraReducers: (builder) => {
    /******************************************************************************************
     *
     *                                  LOAD CLUB GAMES
     *
     ******************************************************************************************
     */
    builder.addCase(
      fetchBasketballClubGames.fulfilled,
      (
        state: BasketballGamesState,
        action: PayloadAction<BasketballClubScheduleGame[]>
      ) => {
        state.clubGames = action.payload;
      }
    );

    builder.addCase(
      fetchBasketballUpcomingClubGames.fulfilled,
      (
        state: BasketballGamesState,
        action: PayloadAction<ResponseBasketballGetClubUpcomingGames>
      ) => {
        state.upcomingClubGames = action.payload;
      }
    );
    /******************************************************************************************
     *
     *                                  LOAD GAME DETAILS
     *
     ******************************************************************************************
     */
    builder.addCase(
      fetchBasketballGame.pending,
      (state: BasketballGamesState) => {
        state.loadingGameDetails = true;
      }
    );
    builder.addCase(
      fetchBasketballGame.fulfilled,
      (state: BasketballGamesState, action: PayloadAction<BasketballGame>) => {
        // TODO Here
        state.gameDetails[action.payload.id] = action.payload;
        state.loadingGameDetails = false;
        if (action.payload.club_details?.judge_suggestions) {
          state.judgeSuggestions[action.payload.id] =
            action.payload.club_details?.judge_suggestions;
        }
      }
    );
    builder.addCase(
      fetchBasketballGame.rejected,
      (state: BasketballGamesState) => {
        state.loadingGameDetails = false;
      }
    );
    builder.addCase(
      saveBasketballGame.fulfilled,
      (state: BasketballGamesState, action: PayloadAction<BasketballGame>) => {
        // TODO Here
        state.gameDetails[action.payload.id] = action.payload;
        if (action.payload.club_details?.judge_suggestions) {
          state.judgeSuggestions[action.payload.id] =
            action.payload.club_details?.judge_suggestions;
        }

        // ClubGames
        state.clubGames = state.clubGames.map((g) => {
          if (g.id === action.payload.id) {
            return {
              ...action.payload,
              game_number: action.payload.game_number + '',
              league: action.payload.league?.name,
            } as BasketballClubScheduleGame;
          }
          return g;
        });
      }
    );
    builder.addCase(
      deleteBasketballGame.fulfilled,
      (state: BasketballGamesState, action: PayloadAction<number>) => {
        delete state.gameDetails[action.payload];

        // ClubGames
        state.clubGames = state.clubGames.filter(
          (g) => g.id !== action.payload
        );
      }
    );
    /******************************************************************************************
     *
     *                                  LOAD SEASON PLANER GAMES
     *
     ******************************************************************************************
     */
    builder.addCase(
      fetchBasketballSeasonPlanerGames.pending,
      (state: BasketballGamesState) => {
        state.seasonPlaner.loading = true;
      }
    );
    builder.addCase(
      fetchBasketballSeasonPlanerGames.fulfilled,
      (
        state: BasketballGamesState,
        action: PayloadAction<BasketballSeasonPlanerResponse>
      ) => {
        const weekOfYear = action.payload.week_of_year;
        const year = action.payload.year;

        state.seasonPlaner.loading = false;
        state.seasonPlaner.pagination = action.payload.pagination;
        state.seasonPlaner.games[`${year}-${weekOfYear}`] = action.payload.data;
        state.seasonPlaner.gym_closures[`${year}-${weekOfYear}`] = action.payload.gym_closures;
        state.seasonPlaner.availableWeeks = action.payload.available_weeks;
        state.seasonPlaner.visible_tasks = action.payload.visible_tasks;
        state.seasonPlaner.currentWeek = {
          weekOfYear: action.payload.week_of_year,
          year: action.payload.year,
          weekAndYear: action.payload.week_and_year,
          from: action.payload.from,
          to: action.payload.to,
        };
      }
    );
    builder.addCase(
      fetchBasketballSeasonPlanerGames.rejected,
      (state: BasketballGamesState) => {
        state.seasonPlaner.loading = false;
      }
    );
    /******************************************************************************************
     *
     *                                  Team Details
     *
     ******************************************************************************************
     */
    builder.addCase(
      assignUsersToBasketballEventTask.fulfilled,
      (
        state: BasketballGamesState,
        action: PayloadAction<BasketballTeamTask>
      ) => {
        const task = action.payload;
        const game_id = task.updated_game_id;
        const team_id = task.team_id;
        if (!game_id) return;
        const tdIndex = teamDetailsIndex(state.gameDetails, game_id, team_id);
        if (tdIndex < 0) return;
        const taskIndex = state.gameDetails[game_id]!.team_details![
          tdIndex
        ].team?.game_tasks!.findIndex(
          (t: BasketballTeamTask) => t.id === task.id
        );
        if (taskIndex == null) return;
        state.gameDetails[game_id]!.team_details![tdIndex].team!.game_tasks![
          taskIndex
        ] = task;
      }
    );
    builder.addCase(
      addTeamCommentToGame.fulfilled,
      (
        state: BasketballGamesState,
        action: PayloadAction<BasketballGameComment>
      ) => {
        const comment = action.payload;
        const game_id = comment.game_id;
        const team_id = comment.team_id;
        const tdIndex = teamDetailsIndex(state.gameDetails, game_id, team_id);
        state.gameDetails[game_id]!.team_details![
          tdIndex
        ].comments?.data.unshift(comment);
      }
    );
    builder.addCase(
      fetchBasketballGameTeamComments.fulfilled,
      (
        state: BasketballGamesState,
        action: PayloadAction<{
          gameId: number;
          teamId: number;
          data: BasketballGameCommentPagination;
        }>
      ) => {
        const data = action.payload;
        const game_id = data.gameId;
        const team_id = data.teamId;
        const tdIndex = teamDetailsIndex(state.gameDetails, game_id, team_id);
        const comments =
          state.gameDetails[game_id]!.team_details![tdIndex].comments;

        state.gameDetails[game_id]!.team_details![tdIndex].comments = {
          ...data.data,
          data: [...(comments?.data || []), ...data.data.data],
        };
      }
    );
    builder.addCase(
      addCarpoolToGame.fulfilled,
      (
        state: BasketballGamesState,
        action: PayloadAction<BasketballTeamGameCarpool>
      ) => {
        const data = action.payload;
        const game_id = data.game_id;
        const team_id = data.team_id;
        const tdIndex = teamDetailsIndex(state.gameDetails, game_id, team_id);

        // check if carpool id already exists - if yes update else push
        const carpoolId = state.gameDetails[game_id].team_details![
          tdIndex
        ].carpool!.findIndex((c) => c.id === data.id);

        if (carpoolId !== -1) {
          state.gameDetails[game_id].team_details![tdIndex].carpool![
            carpoolId
          ] = data;
        } else {
          state.gameDetails[game_id].team_details![tdIndex].carpool!.push(data);
        }
      }
    );
    builder.addCase(
      deleteCarpoolFromGame.fulfilled,
      (state: BasketballGamesState, action: PayloadAction<any>) => {
        const data = action.payload;
        const game_id = data.gameId;
        const team_id = data.teamId;
        const tdIndex = teamDetailsIndex(state.gameDetails, game_id, team_id);
        const carpoolId = state.gameDetails[game_id].team_details![
          tdIndex
        ].carpool!.findIndex((c) => c.id === data.id);
        if (carpoolId !== -1) {
          state.gameDetails[game_id].team_details![tdIndex].carpool!.splice(
            carpoolId,
            1
          );
        }
      }
    );
    builder.addCase(
      updateUserBasketballGameReply.fulfilled,
      (
        state: BasketballGamesState,
        action: PayloadAction<BasketballGameReply>
      ) => {
        const gameId = action.payload.game_id;
        const oldReply = action.payload.old_reply;
        const newReply = action.payload.reply;
        const userId = action.payload.repliable_id;
        const teamDetailsId = action.payload.team_id;

        const oldReplyType =
          oldReply != null
            ? oldReply === BasketballEventReplyAnswer.COMMITMENT
              ? 'yes'
              : 'no'
            : 'unknown';
        const newReplyType =
          newReply != null
            ? newReply === BasketballEventReplyAnswer.COMMITMENT
              ? 'yes'
              : 'no'
            : 'unknown';

        const tdIndex = state.gameDetails[gameId]?.team_details?.findIndex(
          (td) => td.team_id === teamDetailsId
        );

        // tdIndex 0 is allowed
        //if (!tdIndex || tdIndex < 0) return;
        if (tdIndex === undefined || tdIndex < 0) return;
        const replies: BasketballTeamPlayerReplies | undefined =
          state.gameDetails[gameId]?.team_details![tdIndex].team?.playerReplies;

        if (replies) {
          const user = replies[oldReplyType].find(
            (u: BasketballTeamPlayerReply) => u.id === userId
          );

          // Remove old player
          replies[oldReplyType] = replies[oldReplyType].filter(
            (u: BasketballTeamPlayerReply) => u.id !== userId
          );

          // Add player
          if (user) {
            user.reply = action.payload;
            replies[newReplyType].push(user);
          }
        }

        // update user role answer
        const teamId =
          state.gameDetails[gameId]?.team_details![tdIndex].team_id;

        const game = state.gameDetails[gameId];
        const userRoleUserIndex = game?.user_data?.findIndex(
          (u) => u.user.id === userId
        );
        const userRoleRoleIndex = game?.user_data[
          userRoleUserIndex
        ]?.roles.findIndex(
          (r) =>
            r.team_id === teamId &&
            r.type === BasketballEventUserRoleType.PLAYER
        );
        const userRole = game?.user_data[userRoleUserIndex]?.roles[
          userRoleRoleIndex
        ] as BasketballEventUserRolePlayerCoach;

        if (userRole && userRole.reply) {
          userRole.reply.reply = newReply;
        }
      }
    );
    /******************************************************************************************
     *
     *                                  Club Details
     *
     ******************************************************************************************
     */
    builder.addCase(
      setBasketballGameHomeGame.fulfilled,
      (
        state: BasketballGamesState,
        action: PayloadAction<BasketballClubGame>
      ) => {
        state.gameDetails[action.payload.game_id].club_details = action.payload;
      }
    );
    builder.addCase(
      setBasketballGameSetOwnReferees.fulfilled,
      (
        state: BasketballGamesState,
        action: PayloadAction<BasketballClubGame>
      ) => {
        state.gameDetails[action.payload.game_id].club_details = action.payload;
      }
    );
    builder.addCase(
      setBasketballGameJudgeTeam.fulfilled,
      (
        state: BasketballGamesState,
        action: PayloadAction<ResponseBasketballUpdateJudgeTeam>
      ) => {
        // Update game details
        let newJudgeTeam = undefined;
        if (action.payload.team) {
          newJudgeTeam = {
            id: action.payload.team.id ?? 0,
            name: action.payload.team.name ?? '',
          };
        }

        state.judgeSuggestions[action.payload.game_id] =
          action.payload.judge_suggestions;

        // Update judge team in gameDetails
        const gameDetails = state.gameDetails[action.payload.game_id];
        if (gameDetails && gameDetails.club_details) {
          /*gameDetails.club_details.judge_suggestions =
            action.payload.judge_suggestions;*/
          gameDetails.club_details.judge_team_id = action.payload.team?.id;
          gameDetails.club_details.judge_team = newJudgeTeam;
        }
      }
    );
    builder.addCase(
      setBasketballGameJudgeUser.fulfilled,
      (
        state: BasketballGamesState,
        action: PayloadAction<ResponseBasketballUpdateJudgeUser>
      ) => {
        const gameDetails = state.gameDetails[action.payload.game_id];

        if (gameDetails) {
          if (gameDetails.club_details) {
            gameDetails.club_details.judge_paper = action.payload.judge_paper;
            gameDetails.club_details.judge_paper_asst =
              action.payload.judge_paper_asst;
            gameDetails.club_details.judge_time = action.payload.judge_time;
            gameDetails.club_details.judge24_sec = action.payload.judge_24sec;

            gameDetails.club_details.judge_paper_id =
              action.payload.judge_paper?.id;
            gameDetails.club_details.judge_paper_asst_id =
              action.payload.judge_paper_asst?.id;
            gameDetails.club_details.judge_time_id =
              action.payload.judge_time?.id;
            gameDetails.club_details.judge_24sec_id =
              action.payload.judge_24sec?.id;
          }
        }
      }
    );
    builder.addCase(
      setBasketballGameOwnReferee.fulfilled,
      (
        state: BasketballGamesState,
        action: PayloadAction<ResponseBasketballUpdateOwnReferee>
      ) => {
        // Update game details
        const referee1 = action.payload.referee_1;
        const referee2 = action.payload.referee_2;

        const referee1Name = referee1
          ? `${referee1.firstname} ${referee1.lastname}`
          : t('pending');

        const referee2Name = referee2
          ? `${referee2.firstname} ${referee2.lastname}`
          : t('pending');

        const gameDetails = state.gameDetails[action.payload.game_id];
        if (gameDetails) {
          if (gameDetails.club_details) {
            gameDetails.club_details.referee1 = referee1;
            gameDetails.club_details.referee2 = referee2;
            gameDetails.club_details.own_referee1_id = referee1?.id;
            gameDetails.club_details.own_referee2_id = referee2?.id;
          }

          gameDetails.referee_1 = referee1Name;
          gameDetails.referee_2 = referee2Name;
        }

        // Update season planer
        const path = getPathOfSeasonGame(
          state.seasonPlaner.games,
          action.payload.game_id
        );
        if (path) {
          const pathParts = path.split('.');
          const week = pathParts[0];
          const day = pathParts[1];
          const gym = pathParts[2];
          const gameIndex = parseInt(pathParts[3]);

          const seasonPlanerGame =
            state.seasonPlaner.games[week][day][gym][gameIndex];
          if (seasonPlanerGame) {
            seasonPlanerGame.own_referee_1 = referee1;
            seasonPlanerGame.own_referee_2 = referee2;
            seasonPlanerGame.referee1 = referee1Name;
            seasonPlanerGame.referee2 = referee2Name;
          }
        }
      }
    );
    builder.addCase(
      setBasketballGameResponsibleUser.fulfilled,
      (
        state: BasketballGamesState,
        action: PayloadAction<ResponseBasketballUpdateResponsibleUser>
      ) => {
        // Update game details
        const responsibleUser = action.payload.responsible_user;

        const gameDetails = state.gameDetails[action.payload.game_id];
        if (gameDetails) {
          if (gameDetails.club_details) {
            gameDetails.club_details.responsible_user = responsibleUser;
            gameDetails.club_details.responsible_user_id = responsibleUser?.id;
          }
        }

        // Update season planer
        const path = getPathOfSeasonGame(
          state.seasonPlaner.games,
          action.payload.game_id
        );
        if (path) {
          const pathParts = path.split('.');
          const week = pathParts[0];
          const day = pathParts[1];
          const gym = pathParts[2];
          const gameIndex = parseInt(pathParts[3]);

          const seasonPlanerGame =
            state.seasonPlaner.games[week][day][gym][gameIndex];
          if (seasonPlanerGame) {
            seasonPlanerGame.responsible_user = responsibleUser;
          }
        }
      }
    );
    builder.addCase(
      setBasketballTeamGameCars.fulfilled,
      (
        state: BasketballGamesState,
        action: PayloadAction<ResponseBasketballUpdateTeamGameCars>
      ) => {
        // Update game details
        const cars = action.payload.cars;

        const gameDetails = state.gameDetails[action.payload.game_id];
        if (gameDetails && gameDetails.team_details) {
          for (const td of gameDetails.team_details) {
            if (td.team_id === action.payload.team_id) {
              td.cars = action.payload.cars;
            }
          }
        }

        // Update season planer
        const path = getPathOfSeasonGame(
          state.seasonPlaner.games,
          action.payload.game_id
        );
        if (path) {
          const pathParts = path.split('.');
          const week = pathParts[0];
          const day = pathParts[1];
          const gym = pathParts[2];
          const gameIndex = parseInt(pathParts[3]);

          const seasonPlanerGame =
            state.seasonPlaner.games[week][day][gym][gameIndex];
          if (seasonPlanerGame) {
            for (const td of seasonPlanerGame.team_details) {
              if (td.id === action.payload.team_id) {
                td.cars = action.payload.cars;
              }
            }
          }
        }
      }
    );
    builder.addCase(
      setBasketballTeamGameMeetingAt.fulfilled,
      (
        state: BasketballGamesState,
        action: PayloadAction<ResponseBasketballUpdateTeamGameMeetAt>
      ) => {
        // Update game details
        const gameDetails = state.gameDetails[action.payload.game_id];
        if (gameDetails && gameDetails.team_details) {
          for (const td of gameDetails.team_details) {
            if (td.team_id === action.payload.team_id) {
              td.meet_at = action.payload.meet_at;
              td.meet_at_location = action.payload.meet_at_location;
            }
          }
        }
      }
    );
    builder.addCase(
      setBasketballTeamGameNominees.fulfilled,
      (
        state: BasketballGamesState,
        action: PayloadAction<ResponseBasketballUpdateTeamGameNominees>
      ) => {
        // Update game details
        const gameDetails = state.gameDetails[action.payload.game_id];
        if (gameDetails && gameDetails.team_details) {
          for (const td of gameDetails.team_details) {
            if (td.team_id === action.payload.team_id) {
              if (td.team) {
                td.team.playerReplies = action.payload.player_replies;
                td.nominees = action.payload.nominees;
              }
            }
          }
        }
      }
    );
    builder.addCase(
      setBasketballTeamGameHelpouts.fulfilled,
      (state: BasketballGamesState, action: PayloadAction<BasketballGame>) => {
        // Update game details
        const gameDetails = state.gameDetails[action.payload.id];
        if (gameDetails && gameDetails.team_details) {
          state.gameDetails[action.payload.id] = action.payload;
        }
      }
    );
    builder.addMatcher(
      isAnyOf(tasksApi.endpoints.assignToTask.matchFulfilled),
      (
        state: BasketballGamesState,
        action: PayloadAction<ResponseBasePostTaskAssignments>
      ) => {
        const meta = (action as any).meta;
        const originalArgs = meta.arg.originalArgs;
        const taskId = originalArgs.taskId;
        const requestData =
          originalArgs.request as RequestBasePostTaskAssignments;

        // Team Details
        const gameDetails = state.gameDetails[requestData?.game_id ?? 0];
        if (gameDetails && gameDetails.team_details) {
          const teamDetails = gameDetails.team_details.find(
            (td) => td.team_id === requestData.basketball_team_id
          );
          if (teamDetails && teamDetails.team) {
            teamDetails.team.tasks = teamDetails.team.tasks?.map((task) => {
              if (task.id === taskId) {
                return {
                  ...task,
                  assignments: action.payload.assignments,
                };
              }
              return task;
            });
          }
        }

        // Club Details
        if (gameDetails && gameDetails.club_details) {
          gameDetails.club_details.tasks = gameDetails.club_details.tasks?.map(
            (task) => {
              if (task.id === taskId) {
                return {
                  ...task,
                  assignments: action.payload.assignments,
                };
              }
              return task;
            }
          );
        }

        // Update season planer
        const path = getPathOfSeasonGame(
          state.seasonPlaner.games,
          requestData?.game_id ?? 0
        );
        if (path) {
          const pathParts = path.split('.');
          const week = pathParts[0];
          const day = pathParts[1];
          const gym = pathParts[2];
          const gameIndex = parseInt(pathParts[3]);

          //state.seasonPlaner.games["kek"]["kek"]["kek"][1].
          const seasonPlanerGame =
            state.seasonPlaner.games[week][day][gym][gameIndex];
          if (seasonPlanerGame && seasonPlanerGame.tasks) {
            seasonPlanerGame.tasks = seasonPlanerGame.tasks?.map((task) => {
              if (task.id === taskId) {
                return {
                  ...task,
                  assignments: action.payload.assignments,
                };
              }
              return task;
            });
          }
        }

        // Update club games
        const clubGame = state.clubGames.find(
          (g) => g.id === requestData?.game_id
        );

        if (clubGame && taskId) {
          let newClubGameAssignments: {
            [key: string]: {
              [key: string]: TaskAssignment[];
            };
          } = {};

          if (clubGame.task_assignments) {
            newClubGameAssignments = clubGame?.task_assignments;
          }

          const newAssignments: { [key: string]: TaskAssignment[] } = {};
          for (const assignment of action.payload.assignments) {
            if (!newAssignments[assignment.assignable_type]) {
              newAssignments[assignment.assignable_type] = [];
            }
            newAssignments[assignment.assignable_type].push(assignment);
          }

          if (!newClubGameAssignments[taskId]) {
            newClubGameAssignments[taskId] = {};
          }

          newClubGameAssignments[taskId] = newAssignments;
          clubGame.task_assignments = newClubGameAssignments;
        }
      }
    );
    builder.addMatcher(
      isAnyOf(
        eventReportsApi.endpoints.createReport.matchFulfilled,
        eventReportsApi.endpoints.updateReport.matchFulfilled
      ),
      (state: BasketballGamesState, action: PayloadAction<EventReport>) => {
        const eventType = action.payload.eventable_type;
        // search for game via game.clubDetails or game.teamDetails
        const game = Object.values(state.gameDetails).find((g) => {
          if (eventType === EventReportEventableType.BASKETBALL_CLUB_GAME) {
            return g.club_details?.id === action.payload.eventable_id;
          } else {
            return g.team_details?.find(
              (td) => td.id === action.payload.eventable_id
            );
          }
        });

        if (!game) return;

        const gameReport = {
          ...action.payload,
        } as BasketballGameReport;

        if (eventType === EventReportEventableType.BASKETBALL_CLUB_GAME) {
          const reportIndex = state.gameDetails[
            game?.id ?? 0
          ].club_details?.reports?.findIndex((r) => r.id === action.payload.id);
          if (reportIndex !== undefined && reportIndex > -1) {
            state.gameDetails[game?.id ?? 0].club_details?.reports?.splice(
              reportIndex,
              1,
              gameReport
            );
          } else {
            state.gameDetails[game?.id ?? 0].club_details?.reports?.push(
              gameReport
            );
          }
        } else if (
          eventType === EventReportEventableType.BASKETBALL_TEAM_GAME
        ) {
          const teamId = game?.team_details?.find(
            (td) => td.id === action.payload.eventable_id
          )?.team_id;

          if (teamId) {
            const teamIndex = game?.team_details?.findIndex(
              (td) => td.team_id === teamId
            );

            if (teamIndex && teamIndex > -1) {
              const reportIndex = game?.team_details?.[
                teamIndex
              ]?.reports?.findIndex((r) => r.id === action.payload.id);

              if (reportIndex && reportIndex > -1) {
                game?.team_details?.[teamIndex]?.reports?.splice(
                  reportIndex,
                  1,
                  gameReport
                );
              } else {
                game?.team_details?.[teamIndex]?.reports?.push(gameReport);
              }
            }
          }
        }
      }
    );
    builder.addMatcher(
      isAnyOf(eventReportsApi.endpoints.deleteReport.matchFulfilled),
      (state: BasketballGamesState, action: PayloadAction<number>) => {
        const reportId = action.payload;
        const game = Object.values(state.gameDetails).find((g) => {
          return (
            g.club_details?.reports?.find((r) => r.id === reportId) ||
            g.team_details?.find((td) =>
              td.reports?.find((r) => r.id === reportId)
            )
          );
        });

        if (!game) return;

        if (game.club_details?.reports) {
          game.club_details.reports = game.club_details.reports.filter(
            (r) => r.id !== reportId
          );
        }

        if (game.team_details) {
          for (const td of game.team_details) {
            if (td.reports) {
              td.reports = td.reports.filter((r) => r.id !== reportId);
            }
          }
        }
      }
    );
  },
});

/*
 * Export reducer for store configuration.
 */
export const basketballGamesReducer = basketballGamesSlice.reducer;
export const basketballGamesActions = basketballGamesSlice.actions;

const getBasketballGamesState = (rootState: any): BasketballGamesState =>
  rootState[FEATURE_KEY];

export const basketballClubScheduleSelector = createSelector(
  getBasketballGamesState,
  (state) => state.clubGames
);

export const basketballClubScheduleGroupByDateSelector = createSelector(
  getBasketballGamesState,
  (state) => {
    const dates: { [key: string]: BasketballClubScheduleGame[] } = {};

    for (const game of state.clubGames) {
      const date = formatDatabaseDate(game.date);

      if (!dates[date]) {
        dates[date] = [];
      }

      dates[date].push(game);
    }

    const output: { date: string; games: BasketballClubScheduleGame[] }[] = [];

    for (const date in dates) {
      output.push({
        date: date,
        games: dates[date],
      });
    }

    return output;
  }
);

export const basketballClubUpcomingGamesSelector = createSelector(
  getBasketballGamesState,
  (state) => state.upcomingClubGames
);

export const basketballGamesState = createSelector(
  getBasketballGamesState,
  (state) => state
);

export const basketballSeasonPlanerSelector = createSelector(
  getBasketballGamesState,
  (state) => state.seasonPlaner
);

export const basketballGameJudgeSuggestionsSelector = createSelector(
  getBasketballGamesState,
  (state) => state.judgeSuggestions
);

/******************************************************************************************
 *
 *                                  HELPER FUNCTIONS
 *
 ******************************************************************************************
 */
const teamDetailsIndex = (
  games: { [key: string]: BasketballGame },
  gameId: number,
  teamId: number
): number => {
  let returnValue = -1;
  const game = games[gameId];
  if (game) {
    const index = games[gameId].team_details?.findIndex(
      (td) => td.team_id === teamId
    );
    returnValue = index != null ? index : -1;
  }

  return returnValue;
};

const getPathOfSeasonGame = (
  games: any,
  gameId: number
): string | undefined => {
  for (const week in games) {
    for (const day in games[week]) {
      for (const gym in games[week][day]) {
        for (const [index, value] of games[week][day][gym].entries()) {
          if (value.id === gameId) {
            return `${week}.${day}.${gym}.${index}`;
          }
        }
      }
    }
  }

  return undefined;
};
